import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Icon, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, useToast, IconButton, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  useDisclosure, Code, Collapse, Flex,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiTrash2, FiFileText, FiChevronRight,
  FiChevronDown, FiTerminal, FiBox, FiCpu, FiList,
} from 'react-icons/fi';
import ContainerTerminal from './ContainerTerminal';
import ContainerLogTail from './ContainerLogTail';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function Pods() {
  const [pods, setPods] = useState([]);
  const [namespace, setNamespace] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLogs, setSelectedLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Expandable rows – tracks which pod names are expanded
  const [expandedPods, setExpandedPods] = useState(new Set());

  // Active terminals – array of { podName, containerName }
  const [terminals, setTerminals] = useState([]);

  // Active log tails – array of { podName, containerName }
  const [logTails, setLogTails] = useState([]);

  const fetchPods = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/k8s/pods/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setPods(data.pods || []);
        setNamespace(data.namespace || '');
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { fetchPods(); }, [fetchPods]);

  const toggleExpand = (podName) => {
    setExpandedPods(prev => {
      const next = new Set(prev);
      if (next.has(podName)) {
        next.delete(podName);
      } else {
        next.add(podName);
      }
      return next;
    });
  };

  const deletePod = async (podName) => {
    if (!window.confirm(`Delete pod "${podName}"? Kubernetes will recreate it.`)) return;
    try {
      const res = await fetch(`${API_BASE}/k8s/pods/${podName}/delete/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: `Pod ${podName} deleted`, status: 'success', duration: 3000 });
      fetchPods();
    } catch (err) {
      toast({ title: 'Failed to delete pod', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const viewLogs = async (podName) => {
    setLogsLoading(true);
    setSelectedLogs({ pod: podName, logs: '' });
    onOpen();
    try {
      const res = await fetch(`${API_BASE}/k8s/pods/${podName}/logs/?tail=200`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSelectedLogs({ pod: podName, logs: data.logs || 'No logs available' });
    } catch (err) {
      setSelectedLogs({ pod: podName, logs: `Error: ${err.message}` });
    }
    setLogsLoading(false);
  };

  const openTerminal = (podName, containerName) => {
    // Don't open duplicate terminals
    const exists = terminals.find(
      t => t.podName === podName && t.containerName === containerName
    );
    if (exists) {
      toast({
        title: 'Terminal already open',
        description: `${containerName} in ${podName}`,
        status: 'info',
        duration: 2000,
      });
      return;
    }
    setTerminals(prev => [...prev, { podName, containerName }]);
  };

  const closeTerminal = (podName, containerName) => {
    setTerminals(prev =>
      prev.filter(t => !(t.podName === podName && t.containerName === containerName))
    );
  };

  const openLogTail = (podName, containerName) => {
    const exists = logTails.find(
      t => t.podName === podName && t.containerName === containerName
    );
    if (exists) {
      toast({
        title: 'Log tail already open',
        description: `${containerName} in ${podName}`,
        status: 'info',
        duration: 2000,
      });
      return;
    }
    setLogTails(prev => [...prev, { podName, containerName }]);
  };

  const closeLogTail = (podName, containerName) => {
    setLogTails(prev =>
      prev.filter(t => !(t.podName === podName && t.containerName === containerName))
    );
  };

  const phaseColor = (phase) => {
    switch (phase) {
      case 'Running': return 'green';
      case 'Pending': return 'yellow';
      case 'Succeeded': return 'blue';
      case 'Failed': return 'red';
      default: return 'gray';
    }
  };

  const stateColor = (state) => {
    if (!state) return 'gray';
    if (state === 'running') return 'green';
    if (state === 'waiting') return 'yellow';
    if (state === 'terminated') return 'red';
    return 'gray';
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Pods</Heading>
          <Text color="gray.500">
            {pods.length} pods in namespace <Badge colorScheme="blue">{namespace}</Badge>
            {' · '}Click a pod to see its containers
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchPods}>Refresh</Button>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th width="30px" px={2}></Th>
              <Th>Name</Th>
              <Th>Phase</Th>
              <Th>IP</Th>
              <Th>Node</Th>
              <Th>Containers</Th>
              <Th>Restarts</Th>
              <Th>Started</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {pods.map((pod) => {
              const isExpanded = expandedPods.has(pod.name);
              const containers = pod.containers || [];
              const totalRestarts = containers.reduce((sum, c) => sum + (c.restart_count || 0), 0);

              return (
                <React.Fragment key={pod.name}>
                  {/* Pod row */}
                  <Tr
                    _hover={{ bg: 'gray.50' }}
                    cursor="pointer"
                    onClick={() => toggleExpand(pod.name)}
                    bg={isExpanded ? 'blue.50' : undefined}
                    transition="background 0.15s"
                  >
                    <Td px={2} width="30px">
                      <Icon
                        as={isExpanded ? FiChevronDown : FiChevronRight}
                        color={isExpanded ? 'blue.500' : 'gray.400'}
                        boxSize={4}
                        transition="transform 0.15s"
                      />
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Icon as={FiBox} color="gray.400" boxSize={4} />
                        <Text fontWeight="medium" fontSize="sm" isTruncated maxW="250px">
                          {pod.name}
                        </Text>
                      </HStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={phaseColor(pod.phase)}>{pod.phase}</Badge>
                    </Td>
                    <Td><Text fontSize="sm" fontFamily="mono">{pod.pod_ip || '—'}</Text></Td>
                    <Td><Text fontSize="sm" isTruncated maxW="150px">{pod.node_name || '—'}</Text></Td>
                    <Td>
                      <HStack spacing={1} flexWrap="wrap">
                        {containers.map((c, i) => (
                          <Badge key={i} colorScheme={c.ready ? 'green' : 'red'} fontSize="xs">
                            {c.name}
                          </Badge>
                        ))}
                      </HStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={totalRestarts > 0 ? 'orange' : 'gray'} variant="subtle">
                        {totalRestarts}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="xs" color="gray.500">
                        {pod.start_time ? new Date(pod.start_time).toLocaleString() : '—'}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="View Logs">
                          <IconButton
                            icon={<FiFileText />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={(e) => { e.stopPropagation(); viewLogs(pod.name); }}
                            aria-label="View logs"
                          />
                        </Tooltip>
                        <Tooltip label="Delete Pod">
                          <IconButton
                            icon={<FiTrash2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={(e) => { e.stopPropagation(); deletePod(pod.name); }}
                            aria-label="Delete pod"
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>

                  {/* Expanded container list */}
                  {isExpanded && (
                    <Tr>
                      <Td colSpan={9} p={0} borderBottom="2px solid" borderColor="blue.100">
                        <Collapse in={isExpanded} animateOpacity>
                          <Box bg="gray.50" px={6} py={4}>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={3}>
                              Containers ({containers.length})
                            </Text>
                            <VStack spacing={2} align="stretch">
                              {containers.map((container, idx) => (
                                <Box
                                  key={idx}
                                  bg="white"
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor="gray.200"
                                  p={3}
                                  _hover={{ borderColor: 'blue.300', boxShadow: 'sm' }}
                                  transition="all 0.15s"
                                >
                                  <Flex justify="space-between" align="center">
                                    <HStack spacing={4} flex={1}>
                                      <HStack spacing={2} minW="200px">
                                        <Icon as={FiCpu} color="blue.400" boxSize={4} />
                                        <Text fontWeight="medium" fontSize="sm">
                                          {container.name}
                                        </Text>
                                      </HStack>

                                      <Badge
                                        colorScheme={stateColor(container.state)}
                                        fontSize="xs"
                                        minW="70px"
                                        textAlign="center"
                                      >
                                        {container.state || 'unknown'}
                                      </Badge>

                                      <Badge
                                        colorScheme={container.ready ? 'green' : 'red'}
                                        variant="outline"
                                        fontSize="xs"
                                      >
                                        {container.ready ? 'Ready' : 'Not Ready'}
                                      </Badge>

                                      <Text fontSize="xs" color="gray.500" fontFamily="mono" isTruncated maxW="300px">
                                        {container.image || '—'}
                                      </Text>

                                      {container.restart_count > 0 && (
                                        <Badge colorScheme="orange" fontSize="xs" variant="subtle">
                                          {container.restart_count} restart{container.restart_count !== 1 ? 's' : ''}
                                        </Badge>
                                      )}
                                    </HStack>

                                    <HStack spacing={2}>
                                      <Tooltip label={`Tail logs for ${container.name}`}>
                                        <Button
                                          leftIcon={<FiList />}
                                          size="sm"
                                          colorScheme="purple"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openLogTail(pod.name, container.name);
                                          }}
                                          _hover={{ bg: 'purple.50' }}
                                        >
                                          Tail Logs
                                        </Button>
                                      </Tooltip>
                                      <Tooltip label={`Open terminal in ${container.name}`}>
                                        <Button
                                          leftIcon={<FiTerminal />}
                                          size="sm"
                                          colorScheme="teal"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openTerminal(pod.name, container.name);
                                          }}
                                          _hover={{ bg: 'teal.50' }}
                                        >
                                          Terminal
                                        </Button>
                                      </Tooltip>
                                    </HStack>
                                  </Flex>
                                </Box>
                              ))}
                            </VStack>
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Active Terminals */}
      {terminals.length > 0 && (
        <Box mt={8}>
          <HStack mb={4} spacing={2}>
            <Icon as={FiTerminal} color="teal.500" boxSize={5} />
            <Heading size="md">Active Terminals</Heading>
            <Badge colorScheme="teal" fontSize="sm">{terminals.length}</Badge>
          </HStack>
          <VStack spacing={4} align="stretch">
            {terminals.map((t) => (
              <ContainerTerminal
                key={`${t.podName}-${t.containerName}`}
                podName={t.podName}
                containerName={t.containerName}
                onClose={() => closeTerminal(t.podName, t.containerName)}
              />
            ))}
          </VStack>
        </Box>
      )}

      {/* Active Log Tails */}
      {logTails.length > 0 && (
        <Box mt={8}>
          <HStack mb={4} spacing={2}>
            <Icon as={FiList} color="purple.500" boxSize={5} />
            <Heading size="md">Active Log Tails</Heading>
            <Badge colorScheme="purple" fontSize="sm">{logTails.length}</Badge>
          </HStack>
          <VStack spacing={4} align="stretch">
            {logTails.map((t) => (
              <ContainerLogTail
                key={`${t.podName}-${t.containerName}`}
                podName={t.podName}
                containerName={t.containerName}
                onClose={() => closeLogTail(t.podName, t.containerName)}
              />
            ))}
          </VStack>
        </Box>
      )}

      {/* Logs Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Logs: {selectedLogs?.pod}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {logsLoading ? (
              <Box textAlign="center" py={10}><Spinner /></Box>
            ) : (
              <Code
                display="block"
                whiteSpace="pre-wrap"
                p={4}
                bg="gray.900"
                color="green.300"
                borderRadius="md"
                fontSize="xs"
                maxH="60vh"
                overflowY="auto"
              >
                {selectedLogs?.logs}
              </Code>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Pods;
